import { z } from 'zod';
import type { FieldSpec } from '../fields/values.js';

/**
 * Where each column of a file (or key of a webhook payload) goes (M8). A target is the phone, one of
 * the consent evidence columns, the vendor's "do not contact" flag, or a merchant field by key
 * (`field:monthly_revenue`). Unmapped columns are ignored. The merchant's time zone cannot be mapped:
 * imports never write it (M7, the pacer works it out from the state and the area code).
 */

export const SPECIAL_TARGETS = [
  'phone',
  'consent_at',
  'consent_ip',
  'consent_url',
  'consent_text',
  'vendor_dnc',
] as const;
export type SpecialTarget = (typeof SPECIAL_TARGETS)[number];
export type MappingTarget = SpecialTarget | `field:${string}`;

export const SPECIAL_TARGET_LABELS: Record<SpecialTarget, string> = {
  phone: 'Phone',
  consent_at: 'Consent date',
  consent_ip: 'Consent IP address',
  consent_url: 'Consent page URL',
  consent_text: 'Consent wording',
  vendor_dnc: 'Do not contact (vendor flag)',
};

export const MappingTargetSchema = z.union([
  z.enum(SPECIAL_TARGETS),
  z.templateLiteral(['field:', z.string().regex(/^[a-z][a-z0-9_]{0,47}$/)]),
]);

/** A file mapping: column index ("0", "1", …) → target. */
export const ColumnMappingSchema = z.record(z.string().regex(/^\d{1,3}$/), MappingTargetSchema);
export type ColumnMapping = z.infer<typeof ColumnMappingSchema>;

/** A saved mapping (per vendor) or a webhook source's mapping: name (lowercase) → target. */
export const NamedMappingSchema = z.record(z.string().min(1).max(100), MappingTargetSchema);
export type NamedMapping = z.infer<typeof NamedMappingSchema>;

export interface MappableField extends FieldSpec {
  id: string;
  key: string;
  label: string;
  archived: boolean;
}

export function fieldTarget(key: string): MappingTarget {
  return `field:${key}`;
}

export function targetFieldKey(target: MappingTarget): string | null {
  return target.startsWith('field:') ? target.slice('field:'.length) : null;
}

/** What a mapping refers to, resolved against the workspace's fields. */
export interface ResolvedMapping<K> {
  phone: K;
  fields: Array<{ from: K; field: MappableField }>;
  consentAt: K | null;
  consentIp: K | null;
  consentUrl: K | null;
  consentText: K | null;
  vendorDnc: K | null;
}

export type MappingCheck<K> =
  { ok: true; mapping: ResolvedMapping<K> } | { ok: false; error: string };

/**
 * Checks a mapping: exactly one phone column, each target at most once, fields that exist and are not
 * removed, and never the phone field itself.
 */
export function resolveMapping<K>(
  entries: ReadonlyArray<readonly [K, MappingTarget]>,
  fields: readonly MappableField[],
  describe: (from: K) => string,
): MappingCheck<K> {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const used = new Map<MappingTarget, K>();
  const resolved: ResolvedMapping<K | null> = {
    phone: null,
    fields: [],
    consentAt: null,
    consentIp: null,
    consentUrl: null,
    consentText: null,
    vendorDnc: null,
  };
  for (const [from, target] of entries) {
    const previous = used.get(target);
    if (previous !== undefined) {
      return {
        ok: false,
        error: `${describe(previous)} and ${describe(from)} both go to the same place. Choose one.`,
      };
    }
    used.set(target, from);
    const key = targetFieldKey(target);
    if (key !== null) {
      const field = byKey.get(key);
      if (!field || field.archived) {
        return { ok: false, error: `${describe(from)} goes to a field that no longer exists.` };
      }
      if (field.systemKey === 'phone') {
        return { ok: false, error: `Map the phone number to "Phone".` };
      }
      resolved.fields.push({ from, field });
      continue;
    }
    switch (target) {
      case 'phone':
        resolved.phone = from;
        break;
      case 'consent_at':
        resolved.consentAt = from;
        break;
      case 'consent_ip':
        resolved.consentIp = from;
        break;
      case 'consent_url':
        resolved.consentUrl = from;
        break;
      case 'consent_text':
        resolved.consentText = from;
        break;
      case 'vendor_dnc':
        resolved.vendorDnc = from;
        break;
    }
  }
  if (resolved.phone === null)
    return { ok: false, error: 'Choose the column with the phone number.' };
  return { ok: true, mapping: resolved as ResolvedMapping<K> };
}

/** Lowercase, punctuation as spaces: "Phone #", "phone_number" and "Phone Number" all match. */
export function headerKey(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Common header names per target (headerKey form). Field labels and keys always match too. */
const SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  phone: [
    'phone',
    'phone number',
    'phone no',
    'mobile',
    'mobile phone',
    'mobile number',
    'cell',
    'cell phone',
    'cell number',
    'telephone',
    'tel',
    'business phone',
    'contact phone',
    'primary phone',
    'phone 1',
    'phone1',
  ],
  consent_at: [
    'consent date',
    'consent at',
    'consent timestamp',
    'consent time',
    'opt in date',
    'optin date',
    'opt in timestamp',
    'optin timestamp',
    'opt in time',
  ],
  consent_ip: ['ip', 'ip address', 'consent ip', 'opt in ip', 'optin ip'],
  consent_url: ['consent url', 'page url', 'opt in url', 'optin url', 'landing page', 'source url'],
  consent_text: ['consent text', 'consent language', 'tcpa text', 'tcpa language', 'disclosure'],
  vendor_dnc: ['dnc', 'do not contact', 'do not call', 'dnc flag', 'opted out', 'opt out'],
  'field:business_name': [
    'business',
    'business name',
    'company',
    'company name',
    'dba',
    'legal name',
    'business legal name',
    'merchant',
    'merchant name',
  ],
  'field:owner_name': ['owner', 'owner name', 'name', 'full name', 'contact name', 'contact'],
  'field:email': ['email', 'e mail', 'email address'],
  'field:address': ['address', 'street', 'street address', 'business address'],
  'field:state': ['state', 'st', 'business state'],
  'field:monthly_revenue': [
    'revenue',
    'monthly revenue',
    'monthly sales',
    'gross revenue',
    'avg monthly revenue',
    'average monthly revenue',
  ],
  'field:amount_requested': [
    'amount',
    'amount requested',
    'requested amount',
    'loan amount',
    'funding amount',
  ],
  'field:time_in_business_months': ['time in business', 'tib', 'months in business'],
  'field:credit_score_range': ['credit', 'credit score', 'fico'],
  'field:current_positions': ['positions', 'open positions'],
};

/**
 * A first guess at the mapping: a saved vendor mapping wins, then field labels and keys, then common
 * header names. Each target is used once (the first matching column).
 */
export function suggestMapping(
  columns: readonly string[],
  fields: readonly MappableField[],
  saved: NamedMapping = {},
): ColumnMapping {
  const lookup = new Map<string, MappingTarget>();
  for (const [target, names] of Object.entries(SYNONYMS)) {
    for (const name of names) lookup.set(name, target as MappingTarget);
  }
  for (const f of fields) {
    if (f.archived || f.systemKey === 'phone') continue;
    lookup.set(headerKey(f.label), fieldTarget(f.key));
    lookup.set(headerKey(f.key), fieldTarget(f.key));
  }
  const live = new Set(fields.filter((f) => !f.archived).map((f) => fieldTarget(f.key)));
  const valid = (t: MappingTarget) => targetFieldKey(t) === null || live.has(t);
  const savedByKey = new Map(Object.entries(saved).map(([k, v]) => [headerKey(k), v]));

  const result: ColumnMapping = {};
  const taken = new Set<MappingTarget>();
  const pass = (pick: (key: string) => MappingTarget | undefined) => {
    columns.forEach((column, i) => {
      if (result[String(i)]) return;
      const target = pick(headerKey(column));
      if (target && valid(target) && !taken.has(target)) {
        result[String(i)] = target;
        taken.add(target);
      }
    });
  };
  pass((k) => savedByKey.get(k));
  pass((k) => lookup.get(k));
  return result;
}

/** The column mapping of a file, saved per vendor by header name (lowercase). */
export function toNamedMapping(columns: readonly string[], mapping: ColumnMapping): NamedMapping {
  const named: NamedMapping = {};
  for (const [index, target] of Object.entries(mapping)) {
    const column = columns[Number(index)];
    if (column !== undefined) named[headerKey(column)] = target;
  }
  return named;
}
